import { createContext, useContext, useState, useRef, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPinCheck, UserCheck, UserX, DoorClosed, ThumbsDown, Loader2 } from "lucide-react";

const QUEUE_KEY = "jam_checkin_queue";
const loadQueue = () => { try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; } };
const saveQueue = (q) => localStorage.setItem(QUEUE_KEY, JSON.stringify(q));

const OUTCOMES = [
  { v: "met_buyer", label: "Met the buyer", icon: UserCheck, cls: "border-[#A7F3D0] text-[#047857]" },
  { v: "buyer_absent", label: "Buyer wasn't there", icon: UserX, cls: "border-[#FCD34D] text-[#B45309]" },
  { v: "closed", label: "Closed / shut", icon: DoorClosed, cls: "border-[#D1D5DB] text-[#374151]" },
  { v: "not_interested", label: "Not interested today", icon: ThumbsDown, cls: "border-[#FCA5A5] text-[#B91C1C]" },
];

const CheckinContext = createContext(async () => {});
export const useCheckin = () => useContext(CheckinContext);

export function CheckinProvider({ children, onChanged }) {
  const [confirmG, setConfirmG] = useState(null);
  const [pending, setPending] = useState(null); // 1.2 — pre-confirm before writing a visit
  const [sheet, setSheet] = useState(null); // {gid, visit_id, name, needBuyer}
  const [buyer, setBuyer] = useState("");
  const [saving, setSaving] = useState(false);
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  const notify = () => { if (changedRef.current) changedRef.current(); window.dispatchEvent(new Event("jam:data")); };

  const doPost = useCallback(async (g, force) => {
    try {
      const { data } = await api.post(`/garages/${g.id}/checkin`, force ? { force: true } : {});
      toast.success(`Checked in at ${g.name}`);
      setConfirmG(null);
      setBuyer("");
      setSheet({ gid: g.id, visit_id: data.visit_id, name: g.name, needBuyer: data.needs_buyer });
      notify();
    } catch (e) {
      if (e.response?.status === 409 && !force) { setConfirmG(g); return; }
      if (!e.response || !navigator.onLine) {
        const q = loadQueue();
        q.push({ garage_id: g.id, payload: { timestamp: new Date().toISOString() } });
        saveQueue(q);
        toast.success("Saved on your phone — will sync when you have signal");
        setConfirmG(null);
      } else { toast.error(formatApiError(e.response?.data?.detail)); }
    }
  }, []);

  const checkin = useCallback((g) => setPending(g), []);

  const saveOutcome = async (outcome) => {
    if (!sheet) return;
    setSaving(true);
    try {
      if (outcome && sheet.visit_id) await api.patch(`/garages/${sheet.gid}/visits/${sheet.visit_id}`, { outcome });
      if (sheet.needBuyer && buyer.trim()) await api.patch(`/garages/${sheet.gid}`, { buyer_name: buyer.trim() });
      notify();
    } catch { /* visit already saved; outcome is a bonus */ }
    setSaving(false);
    setSheet(null); setBuyer("");
  };

  return (
    <CheckinContext.Provider value={checkin}>
      {children}

      {/* 1.2 — confirm before recording a real visit, showing the Dubai time being stamped */}
      <Dialog open={!!pending} onOpenChange={(v) => { if (!v) setPending(null); }}>
        <DialogContent className="max-w-sm" data-testid="checkin-precheck">
          <DialogHeader><DialogTitle className="text-lg font-black text-[#1E3A8A]">Check in now?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#374151]">
            Record a visit at <b>{pending?.name}</b> now, at{" "}
            <b>{new Date().toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Dubai" })}</b> (Dubai time)?
          </p>
          <div className="flex gap-2 mt-2">
            <button onClick={() => setPending(null)} data-testid="checkin-precheck-cancel"
              className="flex-1 h-12 rounded-lg border border-[#D1D5DB] font-bold text-sm">Cancel</button>
            <button onClick={() => { const g = pending; setPending(null); doPost(g, false); }} data-testid="checkin-precheck-yes"
              className="flex-1 h-12 rounded-lg bg-[#1E3A8A] text-white font-bold text-sm flex items-center justify-center gap-2">
              <MapPinCheck className="w-4 h-4" /> Check in
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmG} onOpenChange={(v) => { if (!v) setConfirmG(null); }}>
        <DialogContent className="max-w-sm" data-testid="checkin-confirm">
          <DialogHeader><DialogTitle className="text-lg font-black text-[#1E3A8A]">Already visited today</DialogTitle></DialogHeader>
          <p className="text-sm text-[#374151]">A visit at <b>{confirmG?.name}</b> is already recorded for today. Record another one?</p>
          <div className="flex gap-2 mt-2">
            <button onClick={() => setConfirmG(null)} className="flex-1 h-11 rounded-lg border border-[#D1D5DB] font-bold text-sm">Cancel</button>
            <button onClick={() => doPost(confirmG, true)} data-testid="checkin-confirm-yes" className="flex-1 h-11 rounded-lg bg-[#1E3A8A] text-white font-bold text-sm">Record it</button>
          </div>
        </DialogContent>
      </Dialog>

      <Sheet open={!!sheet} onOpenChange={(v) => { if (!v) saveOutcome(null); }}>
        <SheetContent side="bottom" className="rounded-t-2xl" data-testid="checkin-outcome-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="text-lg font-black text-[#1E3A8A] flex items-center gap-2">
              <MapPinCheck className="w-5 h-5" /> Checked in — how did it go?
            </SheetTitle>
          </SheetHeader>
          <p className="text-sm text-[#6B7280] -mt-1">{sheet?.name} · the visit is saved either way.</p>
          {sheet?.needBuyer && (
            <div className="mt-3">
              <label className="block text-xs font-bold uppercase text-[#6B7280] mb-1">Buyer's name (if you got it)</label>
              <input value={buyer} onChange={(e) => setBuyer(e.target.value)} data-testid="checkin-buyer-name"
                placeholder="e.g. Ahmed" className="w-full h-12 px-3 rounded-lg border border-[#D1D5DB] focus:outline-none focus:border-[#1E3A8A]" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 mt-3">
            {OUTCOMES.map((o) => (
              <button key={o.v} onClick={() => saveOutcome(o.v)} disabled={saving} data-testid={`checkin-outcome-${o.v}`}
                className={`min-h-[56px] rounded-xl border-2 bg-white font-bold text-sm flex flex-col items-center justify-center gap-1 disabled:opacity-60 ${o.cls}`}>
                <o.icon className="w-5 h-5" /> {o.label}
              </button>
            ))}
          </div>
          <button onClick={() => saveOutcome(null)} disabled={saving} data-testid="checkin-outcome-skip"
            className="w-full h-11 mt-3 rounded-lg text-[#6B7280] font-bold text-sm flex items-center justify-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Skip for now
          </button>
        </SheetContent>
      </Sheet>
    </CheckinContext.Provider>
  );
}
